import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Stack } from '@myphx-engineering/phx-design-system';
import { PharmacyCard, type Pricing } from '@phx/myphx-lib';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import type { PrescriptionOffer } from '../../../../hooks/use-prescription-offers';
import {
    formatToLocalizedCurrency,
    processProviderToPharmacyInfo,
} from '../../../../util';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';
import { sortOffersByPharmacyName } from '../../../../util/offers';

import { ChooseDifferentPharmacyCard } from './ChooseDifferentPharmacyCard';

export type PharmacyOfferListProps = {
    drxId: string;
    offers: PrescriptionOffer[];
    isConfiguredDrug?: boolean;
    showEscapeHatch?: boolean;
};

export const PharmacyOfferList = ({
    drxId,
    offers,
    isConfiguredDrug = false,
    showEscapeHatch = false,
}: PharmacyOfferListProps) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();

    const configOverride = useMedicationConfigOverride();

    return (
        <Stack gap="sm" role="list">
            {offers
                .toSorted(sortOffersByPharmacyName(configOverride))
                .map((offer, index) => {
                    const providerInfo = processProviderToPharmacyInfo(
                        getFragment(offer.provider),
                        configOverride
                    );

                    if (!providerInfo) {
                        return null;
                    }

                    const description =
                        isConfiguredDrug && providerInfo.deliveryDays
                            ? [
                                  t('providerOffers.card.homeDelivery', {
                                      deliveryDays: providerInfo.deliveryDays,
                                  }),
                              ]
                            : providerInfo.shippingTime
                              ? [
                                    t('providerOffers.card.shippingTime', {
                                        shippingTime: providerInfo.shippingTime,
                                    }),
                                ]
                              : [];

                    const pricing: Pricing[] =
                        isConfiguredDrug || offer.patientCost === null
                            ? []
                            : [
                                  {
                                      option: t(
                                          'fill.selectPharmacy.pricing.asLowAs'
                                      ),
                                      value: formatToLocalizedCurrency(
                                          offer.patientCost
                                      ),
                                  },
                              ];

                    const subtitle =
                        !isConfiguredDrug && offer.patientCost === null
                            ? t('fill.fallback.pricing.finalPriceAtPharmacy')
                            : undefined;

                    return (
                        <PharmacyCard
                            key={`offer-${providerInfo.id}-${index}`}
                            title={providerInfo.name}
                            subtitle={subtitle}
                            description={description}
                            pricing={pricing}
                            onClick={() =>
                                navigate({
                                    pathname: getAbsoluteRoute(
                                        'fill.drxId.pharmacy.offer',
                                        {
                                            drxId,
                                            pharmacyId: providerInfo.id,
                                            offerId: offer.id,
                                        }
                                    ),
                                    search: `?${searchParams.toString()}`,
                                })
                            }
                        />
                    );
                })}

            {showEscapeHatch && <ChooseDifferentPharmacyCard drxId={drxId} />}
        </Stack>
    );
};
